import { orgAPI } from "@/api/api";
import { useSyncStatus } from "@liveblocks/react";
import { useEffect, useRef } from "react";
import { useCanvasPermissions } from "./useCanvasPermissions";

const SETTLE_MS = 3_000;
const MIN_INTERVAL_MS = 10_000;

/**
 * Records a Liveblocks history version once this tab's edits have synced and
 * gone quiet, so replay has frames for manual edits too (agent writes snapshot
 * server-side). At most one call per MIN_INTERVAL_MS per tab.
 */
export function useCanvasVersionSnapshots(canvasId: string | undefined) {
  const { canEdit } = useCanvasPermissions();
  const syncStatus = useSyncStatus();
  const sawLocalChangesRef = useRef(false);
  const lastCallRef = useRef(0);

  useEffect(() => {
    if (syncStatus === "synchronizing") {
      sawLocalChangesRef.current = true;
      return;
    }
    if (!canvasId || !canEdit) return;
    if (syncStatus !== "synchronized" || !sawLocalChangesRef.current) return;

    const wait = Math.max(
      SETTLE_MS,
      lastCallRef.current + MIN_INTERVAL_MS - Date.now(),
    );
    const timer = window.setTimeout(() => {
      sawLocalChangesRef.current = false;
      lastCallRef.current = Date.now();
      orgAPI.canvas.createVersion(canvasId).catch((error: unknown) => {
        console.warn("canvas version snapshot failed", error);
      });
    }, wait);
    return () => window.clearTimeout(timer);
  }, [canvasId, canEdit, syncStatus]);
}
